import { CpsCheckboxV2, CpsInput } from "corpus";
import { User1 } from "@repo/icons";
import {
  convertDateTimeIgnoringTimezone,
  formatIgnoringTimezone,
  getCurrentDate,
  stringOrUndefined,
} from "@repo/lib";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { graphql } from "@repo/graphql-types/gql";
import { type FragmentType, useFragment } from "@repo/graphql-types/fragment-masking";
import { useRouteContext, useRouter } from "@tanstack/react-router";
import {
  PatientPageQueryDocument,
  PatientUpdatePageQueryDocument,
} from "@repo/graphql-types/graphql";
import { useMemo } from "react";
import {
  FormControl,
  FormField,
  FormHandlerSubmit,
  FormItem,
  FormRoot,
  FormSubmitButton,
} from "@/components/form.tsx";
import { InputPhone } from "@/components/input-phone.tsx";
import { DatePicker } from "@/components/date-picker.tsx";
import { InputEmail } from "@/components/input-email.tsx";
import { InputCpf } from "@/components/input-cpf.tsx";
import {
  type AppointmentPatientFormValues,
  NUMBER_OF_YEARS_TO_BE_SUBTRACTED_FROM_CURRENT_YEAR,
} from "@/components/appointment-patient-form-body.tsx";
import { patientInfo, patientInfoV2 } from "@/lib/form-schemas/appointment-schema.ts";
import {
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
  useResetQuery,
} from "@/hooks/use-graphql.ts";
import { InputInternationalPhone } from "@/components/input-international-phone";

export const PatientUpdateFormQuery = graphql(/* GraphQL */ `
  fragment PatientUpdateFormQuery on pacientes {
    ativo
    codPaciente
    codTipoConvenio
    cpfProprio
    cpf
    email
    dataNascimento
    nomeConvenio
    nomePlano
    nome
    numeroCarteirinha
    receberWhatsAppConfirmacao
    telefone
    telefoneInternacionalNormalizado
    telefoneProprio
    validadeCarteirinha
  }
`);

export const PatientUpdateMutation = graphql(/* GraphQL */ `
  mutation MyMutation($patientData: PacienteGraphQLInput!) {
    alterarPaciente(input: { paciente: $patientData }) {
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
      pacienteGraphQL {
        ativo
        codPaciente
        codTipoConvenio
        cpfProprio
        cpf
        email
        dataNascimento
        nomeConvenio
        nomePlano
        nome
        numeroCarteirinha
        receberWhatsAppConfirmacao
        telefone
        telefoneInternacionalNormalizado
        telefoneProprio
        validadeCarteirinha
      }
    }
  }
`);

interface PatientUpdateFormProps {
  data: FragmentType<typeof PatientUpdateFormQuery>;
}

export const PatientUpdateForm = ({ data }: PatientUpdateFormProps): JSX.Element => {
  const router = useRouter();
  const patient = useFragment(PatientUpdateFormQuery, data);

  const currentYear = getCurrentDate().getFullYear();
  const initialYear = currentYear - NUMBER_OF_YEARS_TO_BE_SUBTRACTED_FROM_CURRENT_YEAR;

  const { flags } = useRouteContext({
    strict: false,
  });
  const isInternationalPhoneActive = useMemo(
    () => flags["altera-telefone-por-telefone-internacional"],
    [flags],
  );

  const patientSchema = !isInternationalPhoneActive ? patientInfo : patientInfoV2;

  const form = useForm<AppointmentPatientFormValues>({
    resolver: zodResolver(patientSchema),
    defaultValues: {
      patientId: patient.codPaciente,
      cpf: stringOrUndefined(patient.cpf ? patient.cpf : ""),
      dateOfBirth: patient.dataNascimento
        ? convertDateTimeIgnoringTimezone(patient.dataNascimento)
        : undefined,
      email: patient.email ?? "",
      name: patient.nome,
      phone: isInternationalPhoneActive
        ? patient.telefoneInternacionalNormalizado ?? patient.telefone
        : patient.telefone,
      sendConfirmationViaWhatsApp: patient.receberWhatsAppConfirmacao,
    },
  });

  const { mutateAsync } = useGraphQLMutationWithErrorHandler(PatientUpdateMutation, {});
  const invalidatePatientDetails = useInvalidateQuery(PatientUpdatePageQueryDocument);
  const invalidatePatientList = useResetQuery(PatientPageQueryDocument);

  const onSubmit = async (formData: AppointmentPatientFormValues): Promise<void> => {
    const onSuccess = (): void => {
      invalidatePatientDetails();
      invalidatePatientList();
      router.history.back();
    };

    const phoneObject = {
      telefone: isInternationalPhoneActive ? "" : formData.phone,
      telefoneInternacionalNormalizado: isInternationalPhoneActive
        ? formData.phone
        : null,
    };

    await mutateAsync(
      {
        patientData: {
          ativo: true,
          codPaciente: patient.codPaciente,
          codTipoConvenio: patient.codTipoConvenio,
          cpfProprio: patient.cpfProprio,
          cpf: formData.cpf,
          email: formData.email,
          dataNascimento: formData.dateOfBirth
            ? formatIgnoringTimezone(formData.dateOfBirth)
            : null,
          nomeConvenio: patient.nomeConvenio,
          nomePlano: patient.nomePlano,
          nome: formData.name,
          numeroCarteirinha: patient.numeroCarteirinha,
          receberWhatsAppConfirmacao: formData.sendConfirmationViaWhatsApp,
          telefoneProprio: patient.telefoneProprio,
          validadeCarteirinha: patient.validadeCarteirinha,
          ...phoneObject,
        },
      },
      { onSuccess },
    );
  };

  return (
    <FormRoot {...form}>
      <FormHandlerSubmit className="gap-4 m-0" handleSubmit={onSubmit}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <CpsInput
                  required
                  IconLeft={User1}
                  title="Nome completo"
                  placeholder="João da Silva"
                  className="text-neutral-500"
                  inputMode="text"
                  type="text"
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex flex-col gap-2">
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  {!isInternationalPhoneActive ? (
                    <InputPhone
                      required
                      title="Telefone"
                      placeholder="(00) 00000-0000"
                      {...field}
                    />
                  ) : (
                    <InputInternationalPhone
                      required
                      onCountryChange={(value) => form.setValue("phoneCountry", value)}
                      {...field}
                    />
                  )}
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="sendConfirmationViaWhatsApp"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <CpsCheckboxV2
                    className="text-neutral-500 bg-transparent text-md p-0 mx-0 gap-2"
                    defaultChecked={form.getValues("sendConfirmationViaWhatsApp")}
                    label="Enviar WhatsApp para confirmação"
                    {...field}
                    onCheckedChange={field.onChange}
                    value={String(field.value)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="dateOfBirth"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <DatePicker
                  captionLayout="dropdown"
                  fromYear={initialYear}
                  toYear={currentYear}
                  disabledFutureDates={getCurrentDate()}
                  title="Nascimento"
                  className="text-neutral-500"
                  placeholder="dd/mm/aaaa"
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputEmail
                  required
                  title="E-mail"
                  placeholder="Digite aqui"
                  className="text-neutral-500"
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="cpf"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputCpf title="CPF" placeholder="000.000.000-00" {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormSubmitButton type="submit">Salvar</FormSubmitButton>
      </FormHandlerSubmit>
    </FormRoot>
  );
};
